import { useFormContext } from 'react-hook-form';
import { Divider, Stack } from '@optimus-web/core';
import { FormTextareaField, lt, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { ProductResult, Service, useRepositoryQuery } from '@mews-commander/data-access';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { useCommanderSelector } from '@mews-commander/utils';
import { ReservationAsyncSelect } from '../../../../payment/components/ReservationAsyncSelect/ReservationAsyncSelect';
import { useProductsMovedByRoutingRule } from '../hooks/useProductsMovedByRoutingRule';
import { useGetDefaultProductConsumeDate } from '../hooks/useGetDefaultProductConsumeDate';
import { AddProductFormValues, ServiceOrderFormField } from './schema';
import { ProductsSection } from './ProductsSection';
import { ServiceChargeFields } from './Fields/ServiceChargeFields';
import { ProductsNotMovedByRoutingRuleBanner } from './ProductsNotMovedByRoutingRuleBanner';

// brought outside due to perf optimizations, it was causing unecessary re-renders
const label = lt(TextKey.AddServiceToReservationLinkProductToReservationLabel);
const helper = lt(TextKey.AddServiceToReservationLinkProductToReservationHelper);

export const ProductsTab = ({
    accountId,
    service,
    productsData,
    isVirtualBill,
    scopedReservationIds,
}: {
    accountId: string;
    service: Service;
    productsData: ProductResult;
    isVirtualBill: boolean;
    scopedReservationIds?: string[];
}) => {
    const isCompany = useCommanderSelector((state) => isAccountIdCompany(state, { accountId }));
    const { orderRequiresCompletedNotes } = service.options;
    const isNewFinanceLedgersEnabled = useFeatureFlag(FeatureFlag.newFinanceLedgersEnabled);
    const newRoutingRulesPOSEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesPOSEnabled);
    const getDefaultConsumeDate = useGetDefaultProductConsumeDate(accountId, scopedReservationIds);
    const { setValue } = useFormContext<AddProductFormValues>();

    const { data: routingRuleResult } = useRepositoryQuery(
        'routingRules',
        'newGetAll',
        {
            extent: {
                productAssignments: true,
                rateAssignments: false,
                productCategoryAssignments: true,
                accountingCategoryAssignments: true,
                routingRuleAssignments: true,
                profiles: false,
            },
        },
        {
            enabled: newRoutingRulesPOSEnabled && isVirtualBill && !isCompany,
        }
    );

    const { banner, ...productsMovedByRR } = useProductsMovedByRoutingRule(
        accountId,
        service,
        isVirtualBill,
        routingRuleResult
    );

    return (
        <Stack vertical spacing="300">
            {!isCompany && (
                <ReservationAsyncSelect
                    scopedReservationIds={scopedReservationIds}
                    name={ServiceOrderFormField.LinkedReservationId}
                    label={label}
                    helper={helper}
                    accountId={accountId}
                    showFutureStayBanner={isNewFinanceLedgersEnabled}
                    showOnlyUnpaidReservations={false}
                    onChange={(_, reservationId) =>
                        setValue(ServiceOrderFormField.StartUtc, getDefaultConsumeDate(reservationId as string))
                    }
                />
            )}

            {newRoutingRulesPOSEnabled && isVirtualBill ? (
                <>
                    <ProductsNotMovedByRoutingRuleBanner
                        service={service}
                        accountId={accountId}
                        routingRuleResult={routingRuleResult}
                    />
                    {banner}
                </>
            ) : null}

            <ServiceChargeFields service={service} />

            {orderRequiresCompletedNotes && (
                <FormTextareaField label={lt(TextKey.Notes)} name={ServiceOrderFormField.Note} rows={1} required />
            )}

            <Divider />

            <ProductsSection
                service={service}
                accountId={accountId}
                productsData={productsData}
                productsMovedByRoutingRule={productsMovedByRR}
            />
        </Stack>
    );
};
